/**
 * Re-exports from @markeroo/replay package with web app integration.
 *
 * This module wraps the shared replay package components to inject
 * the web app's localized strings and custom Select component.
 *
 * @example
 * ```typescript
 * import { buildPMDocumentState, ReplayContainer } from "@/features/assignments/replay";
 *
 * // Core functions work directly
 * const builder = buildPMDocumentState({ events, schema });
 *
 * // Components are pre-configured with app strings
 * <ReplayContainer events={events} initialText={snapshot} />
 * ```
 */

import {
  ReplayContainer as BaseReplayContainer,
  ReplayViewProseMirror as BaseReplayViewProseMirror,
  ReplayMomentCard as BaseReplayMomentCard,
  ReplayBreadcrumb as BaseReplayBreadcrumb,
  ReplayControls as BaseReplayControls,
  type ReplayContainerProps as BaseReplayContainerProps,
  type ReplayViewProseMirrorProps as BaseReplayViewProseMirrorProps,
  type ReplayMomentCardProps as BaseMomentCardProps,
  type ReplayBreadcrumbProps as BaseBreadcrumbProps,
  type ReplayControlsProps as BaseControlsProps,
} from "@markeroo/replay";
import { ui } from "@/strings";
import { Select } from "@/components/Select";

// Re-export core utilities directly from package
export {
  batchEdits,
  buildPMDocumentState,
  createInitialDocument,
  createEmptyDocument,
  StepApplicationError,
  TIMELINE_SHARED_CONFIG,
  computeTimeContext,
  getTimePosition,
  formatGapDuration,
  formatDuration,
  formatTimestamp,
} from "@markeroo/replay";

// Re-export types directly from package
export type {
  PMDocumentCheckpoint,
  PMDocumentState,
  PMDocumentStateBuilder,
  PMDocumentStateBuilderOptions,
  PMChangeRange,
  ReplayEventType,
  KeyMoment,
  KeyMomentType,
  ChangeType,
  TimeContext,
  PlaybackSpeed,
  MarkJSON,
  CreateInitialDocumentOptions,
} from "@markeroo/replay";

// Re-export simple components directly
export { ReplayProgressBar, VerdictSummary, SPEED_CONFIG } from "@markeroo/replay";
export type { ReplayProgressBarProps, VerdictSummaryProps } from "@markeroo/replay";

// Wrapper for ReplayContainer with app strings and Select
export type ReplayContainerProps = Omit<BaseReplayContainerProps, "strings" | "SelectComponent">;

export function ReplayContainer(props: ReplayContainerProps) {
  return (
    <BaseReplayContainer
      {...props}
      strings={{
        highlightPosition: ui.highlightNavigation.highlightPosition,
        momentProgress: ui.aiDetector.momentProgress,
        noEventsAvailable: ui.aiDetector.noEventsAvailable,
        noContentYet: ui.aiDetector.noContentYet,
      }}
      SelectComponent={Select}
    />
  );
}

// Wrapper for ReplayViewProseMirror with app strings
export type ReplayViewProseMirrorProps = Omit<BaseReplayViewProseMirrorProps, "emptyMessage">;

export function ReplayViewProseMirror(props: ReplayViewProseMirrorProps) {
  return (
    <BaseReplayViewProseMirror
      {...props}
      emptyMessage={ui.aiDetector.noContentYet}
    />
  );
}

// Wrapper for ReplayMomentCard with app strings
export type ReplayMomentCardProps = Omit<BaseMomentCardProps, "strings">;

export function ReplayMomentCard(props: ReplayMomentCardProps) {
  return (
    <BaseReplayMomentCard
      {...props}
      strings={{
        momentTypePaste: ui.aiDetector.momentTypePaste,
        momentTypeTyped: ui.aiDetector.momentTypeTyped,
        momentTypeDeleted: ui.aiDetector.momentTypeDeleted,
        momentTypeSessionStart: ui.aiDetector.momentTypeSessionStart,
        momentTypeUndo: ui.aiDetector.momentTypeUndo,
        thisContainsEdits: ui.aiDetector.thisContainsEdits,
        seeEdits: ui.aiDetector.seeEdits,
        editsLabel: ui.aiDetector.editsLabel,
        highlightPosition: ui.highlightNavigation.highlightPosition,
      }}
    />
  );
}

// Wrapper for ReplayBreadcrumb with app strings
export type ReplayBreadcrumbProps = Omit<BaseBreadcrumbProps, "strings">;

export function ReplayBreadcrumb(props: ReplayBreadcrumbProps) {
  return (
    <BaseReplayBreadcrumb
      {...props}
      strings={{
        keyMoments: ui.aiDetector.keyMoments,
        backToKeyMoments: ui.aiDetector.backToKeyMoments,
        moment: ui.aiDetector.moment,
      }}
    />
  );
}

// Wrapper for ReplayControls with app strings and Select
export type ReplayControlsProps = Omit<BaseControlsProps, "strings" | "SelectComponent">;

export function ReplayControls(props: ReplayControlsProps) {
  return (
    <BaseReplayControls
      {...props}
      strings={{
        playbackControls: ui.aiDetector.playbackControls,
        stepBack: ui.aiDetector.stepBack,
        stepForward: ui.aiDetector.stepForward,
        play: ui.aiDetector.play,
        pause: ui.aiDetector.pause,
        speed: ui.aiDetector.speed,
        playbackSpeed: ui.aiDetector.playbackSpeed,
      }}
      SelectComponent={Select}
    />
  );
}
